import { useCallback } from 'react';
import { useLazyGetSourceByIdQuery } from '../../../api';
import { useTableRowPolling } from '../../../hooks';

/**
 * Source-specific polling hook for the Sources page
 *
 * This hook is a specialized version of useTableRowPolling configured specifically
 * for polling sources with pending or running preflight_state.
 *
 * Features:
 * - Polls sources with preflight_state === 'pending' or 'running'
 * - Stops polling when state becomes 'success' or 'error'
 * - 10-second polling interval with 15-second error retry
 * - Global cleanup function (window.clearSourcePolling)
 *
 * Usage:
 * ```javascript
 * const { pollingCount } = useSourcePolling(tableData, handleSourceUpdate);
 * ```
 *
 * Global cleanup (call on page navigation):
 * ```javascript
 * if (window.clearSourcePolling) {
 *   window.clearSourcePolling();
 * }
 * ```
 *
 * @param {Array} sources - Array of source objects from the table data
 * @param {Function} onSourceUpdate - Callback to update the source in the table data
 *                                    Signature: (sourceId, updatedSourceData) => void
 * @returns {Object} - Object with polling control functions
 *   - startPolling: (sourceId) => void - Start polling a specific source
 *   - clearPolling: (sourceId) => void - Stop polling a specific source
 *   - clearAllPolling: () => void - Stop all polling
 *   - pollingCount: number - Number of sources currently being polled
 */
export const useSourcePolling = (sources, onSourceUpdate) => {
    const [triggerSourceFetch] = useLazyGetSourceByIdQuery();

    // Define source-specific polling configuration
    const shouldPoll = useCallback((source) => {
        return source.preflight_state === 'pending' || source.preflight_state === 'running';
    }, []);

    const pollItem = useCallback(
        async (sourceId) => {
            const result = await triggerSourceFetch(sourceId);
            if (result.error) {
                throw new Error(result.error.message || 'Failed to fetch source');
            }
            return result.data;
        },
        [triggerSourceFetch],
    );

    const shouldContinuePolling = useCallback((updatedSourceData) => {
        const preflightState = updatedSourceData.preflight_state;
        return preflightState === 'pending' || preflightState === 'running';
    }, []);

    // Use the generic polling hook with source-specific configuration
    return useTableRowPolling({
        tableData: sources,
        shouldPoll,
        pollItem,
        onItemUpdate: onSourceUpdate,
        shouldContinuePolling,
        pollInterval: 10000, // 10 seconds
        errorRetryInterval: 15000, // 15 seconds
        globalCleanupFunctionName: 'clearSourcePolling',
        itemIdField: 'id',
        childrenField: 'children',
        isGroupField: 'isGroup',
    });
};
